// NavigateToTrailhead — Button + Modal-Picker für externe Navigations-Apps.
//
// User-Wunsch (Batch 5): "wenn ich auf einen trail klicke soll ein button
// kommen 'navigate to trailhead', und dort soll ein picker aufkommen mit den
// nativen apps die navigation können, google maps, apple maps, etc."
//
// Wave 14al — Erweitert auf START + FINISH:
//   User-Wunsch: "statt 'navigate to start' → 'navigate to start/finish'.
//   Wenn ich es öffne kann ich auswählen ob zum start oder finish, und neben
//   dem navi-button muss ich noch einen share-button haben, um jd per
//   messenger den Ort zu schicken."
//
//   Neuer Flow:
//     1) Tap auf den Button → Endpoint-Picker (Start / Finish)
//     2) Jeder Endpoint zeigt [🧭 Navigate] + [📤 Share]
//     3) Tap auf Navigate → App-Picker für diesen Endpoint (Apple/Google/Waze)
//     4) Tap auf Share → Web Share API (iOS 15.4+, Android Chrome), sonst
//        Clipboard-Fallback mit Toast.
//
// Strategie App-Picker:
//   - Auf iOS: Apple Maps + Google Maps + Waze. Apple Maps via maps.apple.com
//     öffnet die native App (iOS erkennt den Host).
//   - Auf Android: Google Maps + geo:-Scheme (App-Chooser) + Waze.
//   - Auf Desktop/Other: Google Maps + OSM als Fallback.
//   - Wir machen keine native "App-installiert?"-Erkennung (unzuverlässig im
//     Web) — wir öffnen URLs und lassen das OS entscheiden.
//
// Warum Web Share API für die Share-Action?
//   - iOS: öffnet das native Share-Sheet (WhatsApp, iMessage, Signal, Mail,
//     AirDrop). Das will der User genau so.
//   - Android Chrome: öffnet das native Share-Target-System.
//   - Desktop: Chrome unterstützt es auf einigen OSes; sonst fallen wir auf
//     Clipboard zurück (URL + Coords werden kopiert → User pastet in Chat).
//   - AbortError (User hat den Share-Dialog abgebrochen) wird stumm ignoriert
//     — nicht als "hat nicht funktioniert" anzeigen.

import { useState } from 'react';
import type { GpxPoint } from '@adtu/shared';

export interface NavigateToTrailheadProps {
  /** Track-Punkte des Trails. Erster Punkt = Start, letzter Punkt = Finish. */
  points: GpxPoint[];
  /** Optionaler Name für die Zieladresse in den Nav-Apps / Share-Title. */
  label?: string;
  /**
   * Compact-Variante: kürzerer Label-Text, kein "mt-3 w-full" — der Trigger
   * ist als gleichwertiger Button neben "Ride" gedacht und fügt sich in eine
   * flex-row ein. Default false (alte Stack-Variante).
   */
  compact?: boolean;
}

type Platform = 'ios' | 'android' | 'other';
type Endpoint = 'start' | 'finish';
type ModalView = 'endpoint' | 'apps';

function detectPlatform(): Platform {
  const ua = navigator.userAgent || '';
  if (/iP(hone|ad|od)/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

interface NavOption {
  id: string;
  label: string;
  emoji: string;
  /** Baut die URL mit [lat, lon]. Erste URL die vom Browser geöffnet werden
   *  kann wird gestartet. */
  url: (lat: number, lon: number, label?: string) => string;
}

const OPTIONS_IOS: NavOption[] = [
  {
    id: 'apple',
    label: 'Apple Maps',
    emoji: '🧭',
    // maps.apple.com ist universal — öffnet auf iOS die native App, sonst Web.
    url: (lat, lon, label) =>
      `https://maps.apple.com/?daddr=${lat},${lon}${
        label ? `&q=${encodeURIComponent(label)}` : ''
      }&dirflg=d`,
  },
  {
    id: 'gmaps',
    label: 'Google Maps',
    emoji: '🗺️',
    url: (lat, lon) =>
      `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}&travelmode=driving`,
  },
  {
    id: 'waze',
    label: 'Waze',
    emoji: '🚗',
    url: (lat, lon) => `https://waze.com/ul?ll=${lat},${lon}&navigate=yes`,
  },
];

const OPTIONS_ANDROID: NavOption[] = [
  {
    id: 'gmaps',
    label: 'Google Maps',
    emoji: '🗺️',
    // geo:-Scheme öffnet auf Android den App-Picker — genau was wir wollen.
    // Einige Android-Varianten ignorieren &q=; darum schicken wir zusätzlich
    // die Google-Maps-URL als Fallback.
    url: (lat, lon) =>
      `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}&travelmode=driving`,
  },
  {
    id: 'geo',
    label: 'Choose app…',
    emoji: '📍',
    url: (lat, lon, label) =>
      `geo:${lat},${lon}?q=${lat},${lon}${label ? `(${encodeURIComponent(label)})` : ''}`,
  },
  {
    id: 'waze',
    label: 'Waze',
    emoji: '🚗',
    url: (lat, lon) => `https://waze.com/ul?ll=${lat},${lon}&navigate=yes`,
  },
];

const OPTIONS_OTHER: NavOption[] = [
  {
    id: 'gmaps',
    label: 'Google Maps',
    emoji: '🗺️',
    url: (lat, lon) =>
      `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}&travelmode=driving`,
  },
  {
    id: 'osm',
    label: 'OpenStreetMap',
    emoji: '🌍',
    url: (lat, lon) =>
      `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=16/${lat}/${lon}`,
  },
];

/** Build a user-facing share payload for a given endpoint. */
function buildShare(
  point: GpxPoint,
  trailLabel: string | undefined,
  endpoint: Endpoint,
): { title: string; text: string; url: string } {
  const endpointLabel = endpoint === 'start' ? 'Start' : 'Finish';
  const trail = trailLabel ?? 'Trail';
  // google.com/maps/search/?api=1 ist der "offizielle" Share-Link — öffnet
  // auf iOS automatisch Google Maps (oder Apple Maps bei Long-Press), auf
  // Android den üblichen App-Chooser, auf Desktop den Browser. Passt gut als
  // generischer "open this location"-Link im Messenger.
  const url = `https://www.google.com/maps/search/?api=1&query=${point.lat},${point.lon}`;
  const title = `${trail} — ${endpointLabel}`;
  const text = `${trail} (${endpointLabel}): ${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`;
  return { title, text, url };
}

export function NavigateToTrailhead({
  points,
  label,
  compact = false,
}: NavigateToTrailheadProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<ModalView>('endpoint');
  const [activeEndpoint, setActiveEndpoint] = useState<Endpoint>('start');
  // Toast-Text für den Clipboard-Fallback ("Copied to clipboard"). null = kein
  // Toast sichtbar. Auto-clear nach 2s via setTimeout.
  const [toast, setToast] = useState<string | null>(null);

  const start = points[0];
  const finish = points.length > 0 ? points[points.length - 1] : undefined;
  if (!start || !finish) return null;

  // Wave 14al: Loop-Erkennung. Wenn Start und Finish praktisch identisch sind
  // (< ~20m Luftlinie via grober Grad-Distanz), reicht EIN Endpoint. Nicht
  // super-akkurat, aber ausreichend als UX-Hinweis — wir zeigen dann nur
  // "Start/Finish" statt zwei gleiche Zeilen.
  const dLat = Math.abs(start.lat - finish.lat);
  const dLon = Math.abs(start.lon - finish.lon);
  const isLoop = dLat < 0.0002 && dLon < 0.0002;

  const platform = detectPlatform();
  const options =
    platform === 'ios' ? OPTIONS_IOS : platform === 'android' ? OPTIONS_ANDROID : OPTIONS_OTHER;

  const getPoint = (e: Endpoint): GpxPoint => (e === 'start' ? start : finish);

  const openModal = () => {
    setView('endpoint');
    setActiveEndpoint('start');
    setToast(null);
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
  };

  const goToAppPicker = (e: Endpoint) => {
    setActiveEndpoint(e);
    setView('apps');
  };

  const backToEndpointPicker = () => {
    setView('endpoint');
  };

  const launch = (opt: NavOption) => {
    const p = getPoint(activeEndpoint);
    const url = opt.url(p.lat, p.lon, label);
    // Neues Fenster/Tab ist am robustesten: verhindert, dass die PWA selbst
    // "navigiert" wird (sonst verlieren wir u. U. den In-App-State).
    window.open(url, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  const shareLocation = async (e: Endpoint) => {
    const p = getPoint(e);
    const payload = buildShare(p, label, e);

    // 1) Web Share API — öffnet natives Share-Sheet (iOS/Android). Bei User-
    //    Cancel kommt AbortError; den schlucken wir stumm.
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share(payload);
        return;
      } catch (err) {
        const name = (err as { name?: string } | null)?.name;
        if (name === 'AbortError') return; // User hat Share abgebrochen — ok.
        // Sonstige Errors: Fallback zu Clipboard.
      }
    }

    // 2) Clipboard-Fallback — Text + URL, damit der User's Chat-App beim
    //    Einfügen einen sinnvollen Preview hat.
    try {
      const combined = `${payload.text}\n${payload.url}`;
      await navigator.clipboard.writeText(combined);
      setToast('Copied to clipboard');
      setTimeout(() => setToast(null), 2000);
    } catch {
      setToast('Could not share — copy the coordinates manually');
      setTimeout(() => setToast(null), 2500);
    }
  };

  // Compact: content-width (flex-shrink-0 ohne flex-1), damit der Button
  // direkt links neben Ride sitzt und rechts davon Platz für künftige
  // Buttons frei bleibt. User-Wunsch Batch 5: "navigate to start button so
  // klein wie möglich neben ride". Zweizeiliges Label matcht Ride-Höhe via
  // leading-tight.
  // Wave 14al: Label erweitert auf "Navigate / start / finish". Wir nehmen
  // bewusst "start / finish" (mit Spaces) statt "start/finish" — ohne Space
  // kann der Browser den Slash als Wortbreak interpretieren und umbrechen;
  // mit Spaces bleibt es eine saubere Zeile.
  const buttonClass = compact
    ? 'flex-shrink-0 bg-turbo-700/50 hover:bg-turbo-700/70 active:bg-turbo-700/90 transition-colors text-white px-2.5 py-1.5 rounded-lg flex items-center justify-center gap-1.5 border border-turbo-500/30'
    : 'mt-3 w-full bg-turbo-700/50 hover:bg-turbo-700/70 active:bg-turbo-700/90 transition-colors text-white font-medium py-2.5 rounded-lg flex items-center justify-center gap-2 border border-turbo-500/30';

  const activePoint = getPoint(activeEndpoint);
  const activeLabel = activeEndpoint === 'start' ? 'Start' : 'Finish';

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className={buttonClass}
        aria-label="Navigate to start or finish"
      >
        <span className="text-base leading-none flex-shrink-0">🧭</span>
        {compact ? (
          <span className="text-[12px] font-medium leading-tight text-center whitespace-nowrap">
            Navigate
            <br />
            start / finish
          </span>
        ) : (
          <span className="truncate">Navigate to start / finish</span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={view === 'endpoint' ? 'Choose start or finish' : 'Choose navigation app'}
          onClick={closeModal}
        >
          <div
            className="bg-turbo-900 border border-turbo-500/30 rounded-2xl p-4 w-full max-w-sm shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {view === 'endpoint' ? (
              <EndpointView
                trailLabel={label}
                start={start}
                finish={finish}
                isLoop={isLoop}
                onNavigate={goToAppPicker}
                onShare={shareLocation}
                onClose={closeModal}
              />
            ) : (
              <AppsView
                endpointLabel={activeLabel}
                point={activePoint}
                options={options}
                onBack={backToEndpointPicker}
                onClose={closeModal}
                onLaunch={launch}
              />
            )}

            {toast && (
              // Kleiner Inline-Toast im Modal — reicht, weil das Share dort
              // ausgelöst wurde. Kein globaler Toast-Stack nötig.
              <div className="mt-3 text-[12px] text-center text-emerald-300 bg-emerald-900/30 border border-emerald-500/30 rounded-md py-2 px-3">
                {toast}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ---------- Subviews ----------

interface EndpointViewProps {
  trailLabel?: string;
  start: GpxPoint;
  finish: GpxPoint;
  isLoop: boolean;
  onNavigate: (e: Endpoint) => void;
  onShare: (e: Endpoint) => void;
  onClose: () => void;
}

function EndpointView({
  trailLabel,
  start,
  finish,
  isLoop,
  onNavigate,
  onShare,
  onClose,
}: EndpointViewProps) {
  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-lg font-semibold flex-1">Start or finish?</h3>
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 text-xl leading-none"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      {trailLabel && <div className="text-xs text-white/60 mb-3 font-medium">{trailLabel}</div>}

      <EndpointRow
        emoji="🏁"
        title="Start"
        point={start}
        onNavigate={() => onNavigate('start')}
        onShare={() => onShare('start')}
      />

      {isLoop ? (
        <p className="text-[11px] text-white/40 mt-2 leading-relaxed">
          Start and finish are at the same location (loop trail).
        </p>
      ) : (
        <div className="mt-3">
          <EndpointRow
            emoji="🏆"
            title="Finish"
            point={finish}
            onNavigate={() => onNavigate('finish')}
            onShare={() => onShare('finish')}
          />
        </div>
      )}

      <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
        <span className="font-semibold">Navigate</span> opens your maps app with
        driving directions. <span className="font-semibold">Share</span> sends
        the location via your messenger (or copies it to the clipboard).
      </p>
    </>
  );
}

interface EndpointRowProps {
  emoji: string;
  title: string;
  point: GpxPoint;
  onNavigate: () => void;
  onShare: () => void;
}

function EndpointRow({ emoji, title, point, onNavigate, onShare }: EndpointRowProps) {
  return (
    <div className="bg-turbo-700/30 rounded-lg p-3 border border-turbo-500/20">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl leading-none">{emoji}</span>
        <span className="font-semibold flex-1">{title}</span>
        <span className="text-[11px] text-white/50 tabular-nums">
          {point.lat.toFixed(5)}, {point.lon.toFixed(5)}
        </span>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onNavigate}
          className="flex-1 bg-turbo-700/60 hover:bg-turbo-700/80 active:bg-turbo-700/95 transition-colors text-white rounded-md py-2 px-3 flex items-center justify-center gap-1.5 text-sm font-medium"
        >
          <span className="text-base leading-none">🧭</span>
          <span>Navigate</span>
        </button>
        <button
          type="button"
          onClick={onShare}
          className="flex-1 bg-turbo-700/60 hover:bg-turbo-700/80 active:bg-turbo-700/95 transition-colors text-white rounded-md py-2 px-3 flex items-center justify-center gap-1.5 text-sm font-medium"
        >
          <span className="text-base leading-none">📤</span>
          <span>Share</span>
        </button>
      </div>
    </div>
  );
}

interface AppsViewProps {
  endpointLabel: string;
  point: GpxPoint;
  options: NavOption[];
  onBack: () => void;
  onClose: () => void;
  onLaunch: (opt: NavOption) => void;
}

function AppsView({ endpointLabel, point, options, onBack, onClose, onLaunch }: AppsViewProps) {
  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={onBack}
          className="w-10 h-10 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 text-xl leading-none"
          aria-label="Back"
        >
          ←
        </button>
        <h3 className="text-lg font-semibold flex-1">Navigate to {endpointLabel.toLowerCase()}</h3>
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 text-xl leading-none"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
      <div className="text-xs text-white/60 mb-3 tabular-nums">
        {point.lat.toFixed(5)}, {point.lon.toFixed(5)}
      </div>
      <div className="space-y-2">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => onLaunch(opt)}
            className="w-full text-left bg-turbo-700/40 hover:bg-turbo-700/70 active:bg-turbo-700/90 transition-colors rounded-lg px-4 py-3 flex items-center gap-3"
          >
            <span className="text-xl">{opt.emoji}</span>
            <span className="font-medium flex-1">{opt.label}</span>
            <span className="text-white/40">→</span>
          </button>
        ))}
      </div>
      <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
        Opens the chosen app with driving directions. If the app isn't
        installed, your browser will open the web version.
      </p>
    </>
  );
}
